import { assign } from 'xstate';
import { getStorage } from 'firebase/storage';
import { EditCaseContext, EditCaseEvent } from './types';
import * as Str from '../../util/string';
import { uploadSites as uploadSitesFromStorage } from '../../services/firebase/storage';
import { FormValues } from '../../components/case/CaseForm';
import { saveEditCaseDraft, clearEditCaseDraft } from '../../services/sessionStorage/editCaseDraft';

export const loadData = assign<EditCaseContext, EditCaseEvent>(
  (context, ev: any) => {
    const { case: c, selectedPaymentMethod, invoice, draft: loadedDraft } = ev.data;
    const draft = loadedDraft ?? context.pendingDraft;

    const validDraft = draft?.caseId === context.caseId ? draft : null;

    const defaultCaseInfo = {
      ...c,
      submitterEmailAddresses: Str.dedup([
        c?.submitter.email || '',
        ...(c?.submitter.additionalEmails || []),
      ]),
      submitterFirstName: c?.submitter.first!,
      submitterLastName: c?.submitter.last!,
      patientFirstName: c?.patient.first!,
      patientLastName: c?.patient.last!,
    };

    return {
      ...context,
      selectedPaymentMethod: validDraft?.selectedPaymentMethod ?? selectedPaymentMethod,
      invoice,
      case: c,
      caseInfo: validDraft?.caseInfo ?? defaultCaseInfo,
      paymentType: validDraft?.paymentType ?? context.paymentType,
      selectedInvoiceAccountUid: validDraft?.selectedInvoiceAccountUid ?? context.selectedInvoiceAccountUid,
      caseType: validDraft?.caseType ?? context.caseType,
      pendingDraft: undefined,
    };
  }
);

export const loadError = assign<EditCaseContext, EditCaseEvent>({
  error: (context, ev: any) => {
    return ev.data;
  },
});

export const setError = assign<EditCaseContext, EditCaseEvent>({
  error: (context, ev: any) => {
    if (ev.type === 'ERROR') {
      return ev.error;
    }
    console.warn(
      `WARNING: #setError action expected ERROR event but received ${ev.type}`
    );
    return context.error;
  },
});

export const setUploadedCaseInfo = assign<EditCaseContext, EditCaseEvent>({
  caseInfo: (context, event: any) => {
    const uploadedSites = event.data;
    return {
      ...context.caseInfo!,
      sites: uploadedSites,
    };
  },
});

function calculatePercent(num: number, total: number) {
  return Math.min((num + 1) / total, 0.99);
}

export const uploadSitesWithProgress = async (context: EditCaseContext, send: any) => {
  if (!context.caseInfo || !context.userId) {
    throw new Error('Missing case info or user ID');
  }

  try {
    const storage = getStorage(context.firebaseApp);
    
    // Check if sites need to be uploaded
    const needsUpload = context.caseInfo.sites.some((site) =>
      site.images.some((img) => {
        if (img instanceof File) return true;
        // Also check for failed uploads that need retry
        if (typeof img === 'object' && 'status' in img && img.status === 'failed' && img.originalFile) {
          return true;
        }
        return false;
      })
    );
    let caseInfo: FormValues = context.caseInfo;

    if (needsUpload) {
      send({
        type: 'SET_UPLOAD_PROGRESS',
        data: {
          message: 'Uploading files...',
          percent: 0,
        },
      });

      const numTotalAttachments = context.caseInfo.sites.reduce(
        (acc, site) => {
          const fileCount = site.images.filter((img) => {
            if (img instanceof File) return true;
            // Count failed uploads that need retry
            if (typeof img === 'object' && 'status' in img && img.status === 'failed' && img.originalFile) {
              return true;
            }
            return false;
          }).length;
          return acc + fileCount;
        },
        0
      );

      // Upload sites with progress tracking
      const uploadResult = await uploadSitesFromStorage(
        storage,
        context.caseInfo.sites,
        context.userId,
        (num, total, name, siteIndex) => {
          const siteNum = siteIndex + 1;
          send({
            type: 'SET_UPLOAD_PROGRESS',
            data: {
              message: `Uploaded ${
                num + 1
              } of ${numTotalAttachments} files (site #${siteNum})`,
              percent: calculatePercent(num, numTotalAttachments),
            },
          });
        }
      );

      // Send completion message (indicate failures if any)
      send({
        type: 'SET_UPLOAD_PROGRESS',
        data: {
          message: uploadResult.hasFailures
            ? `Upload complete with ${uploadResult.failedCount} failure(s)`
            : 'Upload complete!',
          percent: 1,
        },
      });

      // Return sites regardless of failures so ImageStatus objects are preserved
      return uploadResult.sites;
    }
    
    // Return existing sites if no upload needed
    return context.caseInfo.sites;
  } catch (error) {
    console.error('Error uploading sites:', error);
    throw error;
  }
};

export const saveDraft = (context: EditCaseContext) => {
  if (!context.caseId) return;

  saveEditCaseDraft(context.caseId, {
    caseId: context.caseId,
    caseInfo: context.caseInfo,
    paymentType: context.paymentType,
    selectedPaymentMethod: context.selectedPaymentMethod,
    selectedInvoiceAccountUid: context.selectedInvoiceAccountUid,
    caseType: context.caseType,
  });
};

export const clearDraft = (context: EditCaseContext) => {
  if (!context.caseId) return;
  clearEditCaseDraft(context.caseId);
};

export const storeDraft = assign<EditCaseContext, EditCaseEvent>({
  pendingDraft: (context, ev: any) => ev.data,
});

export * from '../submitCase/actions';
